import { Badge } from "@/components/ui/badge";
import { useCurrency } from "@/context/CurrencyContext";
import {
  MapPin, Bus,
  Coffee, Library, Dumbbell, Beer, Utensils, Laptop,
  Home, Heart, MessageCircle, Wallet,
  ShieldCheck, ClipboardList, CloudSun, Church, Soup,
  Plane, Briefcase, FileCheck, CalendarDays, Stethoscope, AlertTriangle,
} from "lucide-react";

const currencySymbol = (c) => ({
  GBP: "£", USD: "$", EUR: "€", CAD: "C$", AUD: "A$",
  NZD: "NZ$", SGD: "S$", HKD: "HK$", CHF: "CHF ", DKK: "kr",
  JPY: "¥", KRW: "₩", CNY: "¥", INR: "₹", AED: "AED ", CZK: "Kč", SEK: "kr",
  NOK: "kr", PLN: "zł", TWD: "NT$", ILS: "₪",
}[c] || "");

export function SafetySection({ safety }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-8" data-testid="city-safety">
      <div className="rounded-2xl border-l-4 border-emerald-400 bg-emerald-50/70 p-5 sm:p-7">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-editorial text-2xl flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-emerald-600" /> Safety pulse
        </h2>
        <div className="flex items-center gap-1 text-emerald-600" data-testid="city-safety-score">
          {[...Array(5)].map((_, i) => (
            <ShieldCheck key={i} className={`h-4 w-4 ${i < (safety.score || 0) ? "fill-emerald-100 text-emerald-500" : "text-stone-300"}`} />
          ))}
          <span className="text-xs text-stone-600 ml-2 font-mono">{safety.score}/5</span>
        </div>
      </div>
      <p className="text-stone-700 leading-relaxed max-w-4xl mb-4">{safety.summary}</p>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
          <h3 className="font-editorial text-lg mb-2 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" /> Where to be extra aware
          </h3>
          <ul className="space-y-1.5 text-sm text-amber-900">
            {(safety.areas_caution || []).map((a, i) => (
              <li key={i} className="flex items-start gap-2"><span className="text-amber-600 font-bold mt-0.5">·</span><span>{a}</span></li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-stone-200 rounded-lg p-5">
          <h3 className="font-editorial text-lg mb-2 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600" /> Campus security
          </h3>
          <p className="text-sm text-stone-700 leading-relaxed">{safety.campus_security}</p>
        </div>
      </div>
      </div>
    </section>
  );
}

export function ArrivalChecklistSection({ cityName, arr }) {
  const items = [
    { key: "sim", label: "SIM card", value: arr.sim, icon: MessageCircle },
    { key: "bank", label: "Student bank account", value: arr.bank, icon: Wallet },
    { key: "gp_or_doctor", label: "GP / doctor registration", value: arr.gp_or_doctor, icon: Stethoscope },
    { key: "transport_card", label: "Transport card", value: arr.transport_card, icon: Bus },
    { key: "cheap_essentials", label: "Cheap essentials", value: arr.cheap_essentials, icon: Home },
  ];
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-8" data-testid="city-arrival">
      <div className="rounded-2xl border-l-4 border-indigo-400 bg-indigo-50/70 p-5 sm:p-7">
      <h2 className="font-editorial text-2xl mb-3 flex items-center gap-2">
        <ClipboardList className="h-5 w-5 text-[#002147]" /> First 72 hours in {cityName}
      </h2>
      <p className="text-sm text-stone-600 max-w-3xl mb-5">Knock these five items off in your first three days and everything else falls into place.</p>
      <div className="grid md:grid-cols-2 gap-3">
        {items.filter((i) => i.value).map((i) => {
          const Icon = i.icon;
          return (
            <div key={i.key} className="bg-white border border-stone-200 rounded-lg p-4 flex gap-3" data-testid={`city-arrival-${i.key}`}>
              <Icon className="h-4 w-4 text-[#002147] mt-1 shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">{i.label}</div>
                <p className="text-sm text-stone-800 leading-relaxed">{i.value}</p>
              </div>
            </div>
          );
        })}
      </div>
      </div>
    </section>
  );
}

export function WeatherSection({ cityName, weather }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-8" data-testid="city-weather">
      <div className="rounded-2xl border-l-4 border-sky-400 bg-sky-50/70 p-5 sm:p-7">
      <h2 className="font-editorial text-2xl mb-3 flex items-center gap-2">
        <CloudSun className="h-5 w-5 text-sky-500" /> Weather in {cityName}
      </h2>
      <p className="text-sm text-stone-700 max-w-3xl mb-5">{weather.summary}</p>
      <div className="bg-white border border-stone-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-200 text-stone-500 uppercase tracking-wider text-xs">
              <th className="text-left p-3 font-normal">Month</th>
              <th className="text-left p-3 font-normal">Temp (°C)</th>
              <th className="text-left p-3 font-normal">Rain (mm)</th>
              <th className="text-left p-3 font-normal">Notable</th>
            </tr>
          </thead>
          <tbody>
            {weather.months.map((m) => (
              <tr key={m.name} className="border-b border-stone-50 last:border-0">
                <td className="p-3 font-mono text-xs uppercase text-stone-500">{m.name}</td>
                <td className="p-3 font-mono">{m.temp_c_lo}° – {m.temp_c_hi}°</td>
                <td className="p-3 font-mono text-sky-700">{m.rain_mm}</td>
                <td className="p-3 text-stone-700 text-xs">{m.notable}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </section>
  );
}

export function FaithSection({ faith }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-8" data-testid="city-faith">
      <div className="rounded-2xl border-l-4 border-violet-400 bg-violet-50/70 p-5 sm:p-7">
      <h2 className="font-editorial text-2xl mb-3 flex items-center gap-2">
        <Church className="h-5 w-5 text-[#4C1D95]" /> Faith &amp; community
      </h2>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-stone-200 rounded-lg p-5">
          <h3 className="font-editorial text-lg mb-3">Places of worship near campus</h3>
          <ul className="space-y-2 text-sm">
            {(faith.places_of_worship || []).map((p, i) => (
              <li key={i} className="flex items-start gap-2" data-testid={`city-faith-place-${i}`}>
                <span className="text-[#4C1D95] font-bold mt-0.5">·</span>
                <div>
                  <span className="font-semibold">{p.name}</span>
                  <span className="text-stone-500"> · <Badge variant="outline" className="text-[10px] uppercase">{p.type}</Badge> · {p.area}</span>
                </div>
              </li>
            ))}
          </ul>
          <h3 className="font-editorial text-lg mt-6 mb-2">Cultural student societies</h3>
          <ul className="space-y-1 text-sm text-stone-700">
            {(faith.cultural_societies || []).map((s, i) => (
              <li key={i} className="flex items-start gap-2"><span className="text-stone-400 mt-0.5">·</span><span>{s}</span></li>
            ))}
          </ul>
        </div>
        <div className="bg-gradient-to-br from-pink-50 to-purple-50 border border-pink-200 rounded-lg p-5" data-testid="city-lgbtq">
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="font-editorial text-lg">LGBTQ+ scene</h3>
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Heart key={i} className={`h-3.5 w-3.5 ${i < (faith.lgbtq?.score || 0) ? "fill-pink-500 text-pink-500" : "text-stone-300"}`} />
              ))}
            </div>
          </div>
          <p className="text-sm text-stone-700 leading-relaxed">{faith.lgbtq?.note}</p>
        </div>
      </div>
      </div>
    </section>
  );
}

export function FoodFromHomeSection({ food }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-8" data-testid="city-food">
      <div className="rounded-2xl border-l-4 border-amber-400 bg-amber-50/70 p-5 sm:p-7">
      <h2 className="font-editorial text-2xl mb-3 flex items-center gap-2">
        <Soup className="h-5 w-5 text-orange-500" /> Food from home
      </h2>
      <div className="grid md:grid-cols-3 gap-4 mb-5">
        {[
          { key: "halal",  label: "Halal",  value: food.halal,  colour: "emerald" },
          { key: "kosher", label: "Kosher", value: food.kosher, colour: "sky" },
          { key: "vegan",  label: "Vegan / vegetarian", value: food.vegan,  colour: "lime" },
        ].filter((c) => c.value).map((c) => (
          <div key={c.key} className="bg-white border border-stone-200 rounded-lg p-5" data-testid={`city-food-${c.key}`}>
            <div className={`text-xs uppercase tracking-wider text-${c.colour}-700 mb-2 font-semibold`}>{c.label}</div>
            <p className="text-sm text-stone-700 leading-relaxed">{c.value}</p>
          </div>
        ))}
      </div>
      {food.grocery_corridors?.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-lg p-5" data-testid="city-food-corridors">
          <h3 className="font-editorial text-lg mb-3">Grocery corridors worth knowing</h3>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-stone-700">
            {food.grocery_corridors.map((c, i) => (
              <li key={i} className="flex items-start gap-2"><span className="text-orange-500 font-bold mt-0.5">·</span><span>{c}</span></li>
            ))}
          </ul>
        </div>
      )}
      </div>
    </section>
  );
}

export function GettingHomeSection({ cityName, gh }) {
  const { formatFrom } = useCurrency();
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-8" data-testid="city-getting-home">
      <div className="rounded-2xl border-l-4 border-teal-400 bg-teal-50/70 p-5 sm:p-7">
      <h2 className="font-editorial text-2xl mb-3 flex items-center gap-2">
        <Plane className="h-5 w-5 text-[#002147]" /> Getting home from {cityName}
      </h2>
      <p className="text-sm text-stone-600 mb-4">Hub airport: <span className="font-semibold text-stone-900">{gh.hub_airport}</span>. Prices are typical round-trip in low season — expect +30% for Christmas / Diwali / Chinese New Year.</p>
      <div className="bg-white border border-stone-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-200 text-stone-500 uppercase tracking-wider text-xs">
              <th className="text-left p-3 font-normal">Country</th>
              <th className="text-left p-3 font-normal">City</th>
              <th className="text-left p-3 font-normal">Direct?</th>
              <th className="text-left p-3 font-normal">Return ticket (low season)</th>
            </tr>
          </thead>
          <tbody>
            {gh.destinations.map((d, i) => (
              <tr key={i} className="border-b border-stone-50 last:border-0" data-testid={`city-getting-home-${i}`}>
                <td className="p-3">{d.country}</td>
                <td className="p-3">{d.city}</td>
                <td className="p-3">
                  {d.direct ? (
                    <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-300">Direct</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px]">1 stop</Badge>
                  )}
                </td>
                <td className="p-3 font-mono">
                  {currencySymbol(d.currency)}{d.return_ticket_native.toLocaleString()}
                  <span className="text-[10px] text-stone-500 ml-2">≈ {formatFrom(d.return_ticket_native, d.currency)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </section>
  );
}

export function PartTimeWorkSection({ cityName, work }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-8" data-testid="city-work">
      <div className="rounded-2xl border-l-4 border-lime-500 bg-lime-50/70 p-5 sm:p-7">
      <h2 className="font-editorial text-2xl mb-3 flex items-center gap-2">
        <Briefcase className="h-5 w-5 text-[#002147]" /> Part-time work in {cityName}
      </h2>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-stone-200 rounded-lg p-5">
          <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">Visa rules for international students</div>
          <p className="text-sm text-stone-800 leading-relaxed mb-4">{work.visa_rules}</p>
          {work.where_students_work?.length > 0 && (
            <>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2">Where students actually work</h3>
              <ul className="space-y-1 text-sm text-stone-700">
                {work.where_students_work.map((s, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="text-emerald-500 font-bold mt-0.5">·</span><span>{s}</span></li>
                ))}
              </ul>
            </>
          )}
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5">
          <div className="text-xs uppercase tracking-wider text-emerald-700 mb-1">Typical hourly wage</div>
          <div className="font-chip text-2xl text-emerald-900 mb-3">{currencySymbol(work.currency)}{work.typical_hourly_native}/hr</div>
          {work.platforms?.length > 0 && (
            <>
              <div className="text-xs uppercase tracking-wider text-emerald-700 mb-1 mt-4">Where to find jobs</div>
              <ul className="space-y-1 text-sm text-emerald-900">
                {work.platforms.map((p, i) => <li key={i}>· {p}</li>)}
              </ul>
            </>
          )}
        </div>
      </div>
      </div>
    </section>
  );
}

export function PostStudyVisaSection({ visa }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-10" data-testid="city-post-study-visa">
      <div className="bg-gradient-to-r from-[#002147] to-[#4C1D95] text-white rounded-lg p-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-white/70 mb-2">
          <FileCheck className="h-3.5 w-3.5" /> Post-study work visa
        </div>
        <h2 className="font-editorial text-2xl mb-2">{visa.route}</h2>
        <div className="text-sm text-white/80 mb-3">Duration: <span className="font-mono font-semibold text-white">{visa.duration_years}</span></div>
        <p className="text-sm text-white/90 leading-relaxed max-w-4xl">{visa.summary}</p>
        <div className="mt-4 text-[10px] text-white/60 italic">Rules change — always verify with the official government portal before applying.</div>
      </div>
    </section>
  );
}

export function AnnualEventsSection({ cityName, events }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-8" data-testid="city-events">
      <div className="rounded-2xl border-l-4 border-rose-400 bg-rose-50/70 p-5 sm:p-7">
      <h2 className="font-editorial text-2xl mb-3 flex items-center gap-2">
        <CalendarDays className="h-5 w-5 text-rose-500" /> Annual event calendar
      </h2>
      <p className="text-sm text-stone-600 mb-4">Time your first year around these — the ones you'll remember.</p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {events.map((e, i) => (
          <div key={i} className="bg-white border border-stone-200 rounded-lg p-4" data-testid={`city-event-${i}`}>
            <div className="flex items-baseline justify-between gap-2 mb-1">
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{e.month}</Badge>
            </div>
            <div className="font-semibold text-sm mt-1">{e.name}</div>
            {e.note && <p className="text-xs text-stone-600 mt-1 leading-relaxed">{e.note}</p>}
          </div>
        ))}
      </div>
      </div>
    </section>
  );
}

export function WellbeingSection({ wellbeing }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-8" data-testid="city-wellbeing">
      <div className="rounded-2xl border-l-4 border-pink-400 bg-pink-50/70 p-5 sm:p-7">
      <h2 className="font-editorial text-2xl mb-3 flex items-center gap-2">
        <Stethoscope className="h-5 w-5 text-emerald-600" /> Health &amp; wellbeing
      </h2>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-stone-200 rounded-lg p-5">
          <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">GP / doctor registration</div>
          <p className="text-sm text-stone-800 leading-relaxed mb-4">{wellbeing.gp_or_doctor}</p>
          <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">University counselling</div>
          <p className="text-sm text-stone-800 leading-relaxed">{wellbeing.uni_counselling}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-5">
          <div className="text-xs uppercase tracking-wider text-red-700 mb-2 font-semibold">Crisis &amp; support hotlines</div>
          <ul className="space-y-1 text-sm text-red-900">
            {(wellbeing.external || []).map((s, i) => (
              <li key={i} className="flex items-start gap-2"><span className="text-red-600 font-bold mt-0.5">·</span><span>{s}</span></li>
            ))}
          </ul>
          <div className="mt-4 text-xs uppercase tracking-wider text-red-700 font-semibold mb-1">Emergency</div>
          <p className="text-sm text-red-900 leading-relaxed">{wellbeing.emergency}</p>
        </div>
      </div>
      </div>
    </section>
  );
}
